import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import Seo from "../components/site/Seo";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "", product_interest: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [products, setProducts] = useState([]);
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "", product_interest: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — Reptile Global"
        description="Talk to the Reptile Global team about your project or product."
        organizationSchema={settings?.organization_schema}
      />
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Contact</div>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-zinc-950 leading-[0.95]">
              Let's <span className="text-orange-500">build</span> something together.
            </h1>
            <p className="mt-6 text-lg text-zinc-600">Whether it's a product, a service or a partnership — we'd love to hear from you.</p>

            <div className="mt-10 space-y-4 text-sm text-zinc-700">
              {settings?.contact_email && (
                <div className="flex items-center gap-3"><Mail size={18} className="text-orange-500" /> {settings.contact_email}</div>
              )}
              {settings?.contact_phone && (
                <div className="flex items-center gap-3"><Phone size={18} className="text-orange-500" /> {settings.contact_phone}</div>
              )}
              {settings?.address && (
                <div className="flex items-center gap-3"><MapPin size={18} className="text-orange-500" /> {settings.address}</div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-zinc-200 rounded-2xl p-8 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your name" name="name" value={form.name} onChange={onChange} required />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                <Field label="Company" name="company" value={form.company} onChange={onChange} />
              </div>
              <Field label="Subject" name="subject" value={form.subject} onChange={onChange} />
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Product of interest</label>
                <select
                  name="product_interest"
                  value={form.product_interest}
                  onChange={onChange}
                  data-testid="contact-product-interest"
                  className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500"
                >
                  <option value="">Select (optional)</option>
                  {products.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                  <option value="Custom Development">Custom Development / IT Services</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Message</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3.5 rounded-full inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Sending..." : "Send Message"}
              </button>
              {done && <div className="text-green-600 text-sm font-semibold" data-testid="contact-success">Thank you! We'll be in touch shortly.</div>}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">{label}{required && " *"}</label>
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={`contact-field-${name}`}
      className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500"
    />
  </div>
);

export default Contact;
